import AppModal from '@/components/AppModal/AppModal';
import { config } from '@/config';
import { ORDER_REPORT_CSV, ORDER_REPORT_CSV_TYPE } from '@/constants/enum';
import { useExportOrderReportCSVMutation } from '@/redux/query';
import { orderFilterSettingSelector, orderPaginationSettingSelector } from '@/redux/slice/order.slice';
import { ChoiceList } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  open: boolean;
  onCancel: () => void;
}

export const ExportOrderModal = ({ open, onCancel }: Props) => {
  const { t } = useTranslation('orderReport');
  const [selected, setSelected] = useState<ORDER_REPORT_CSV[]>([ORDER_REPORT_CSV.CURRENT_PAGE]);
  const handleChange = useCallback((value: ORDER_REPORT_CSV[]) => setSelected(value), []);
  const orderPaginationSettings = useSelector(orderPaginationSettingSelector);
  const orderFilterSettings = useSelector(orderFilterSettingSelector);

  const [exportOrderReportCSV, { isLoading: isLoadingExportCSV }] = useExportOrderReportCSVMutation();

  const exportCSVType = useMemo(() => {
    switch (selected[0]) {
      case ORDER_REPORT_CSV.ALL_PAGE:
        return ORDER_REPORT_CSV_TYPE.ALL_PAGE;
      case ORDER_REPORT_CSV.CURRENT_PAGE:
        return ORDER_REPORT_CSV_TYPE.CURRENT_PAGE;
      default:
        return ORDER_REPORT_CSV_TYPE.SPECIFIC;
    }
  }, [selected]);

  const handleExportOrderReportCSV = () => {
    exportOrderReportCSV({
      shop: config.shop,
      limit: selected[0] === ORDER_REPORT_CSV.CURRENT_PAGE ? orderPaginationSettings.perPage : 0,
      offset:
        selected[0] === ORDER_REPORT_CSV.CURRENT_PAGE ? (orderPaginationSettings.page - 1) * orderPaginationSettings.perPage : 0,
      type: exportCSVType,
      orderType: orderFilterSettings.tab,
    })
      .unwrap()
      .then((res) => {
        const url = window.URL.createObjectURL(res.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'orders_report.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => console.log(err));
  };

  return (
    <AppModal
      open={open}
      onCancel={onCancel}
      onSubmit={handleExportOrderReportCSV}
      title={t('reportTable.exportOrders.title')}
      submitText={t('reportTable.exportOrders.submit')}
      loading={isLoadingExportCSV}
    >
      <ChoiceList
        title={t('reportTable.exportOrders.title')}
        titleHidden
        choices={[
          { label: t('reportTable.exportOrders.currentChoice'), value: 'current' },
          { label: t('reportTable.exportOrders.allChoice'), value: 'all' },
        ]}
        selected={selected}
        onChange={handleChange}
      />
    </AppModal>
  );
};
